import React, { useState, useEffect, useCallback } from 'react';
import {
  EyeIcon,
  MagnifyingGlassIcon,
  TruckIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
  CurrencyDollarIcon,
  ShoppingBagIcon,
  CreditCardIcon,
  PhotoIcon,
  ChevronDownIcon,
  ArrowPathIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import { apiService } from '../../services/api';
import ManualOrderItemsEditor, { EditorLine } from '../../components/admin/ManualOrderItemsEditor';

interface Order {
  id: number;
  order_number: string;
  user_id: number | null;
  source?: string;
  customer_name?: string;
  customer_phone?: string;
  customer_email?: string;
  customer_address?: string;
  status: string;
  subtotal: number;
  tax_amount: number;
  shipping_amount: number;
  delivery_fee: number;
  total_amount: number;
  payment_status: string;
  payment_method: string;
  payment_screenshot?: string;
  payment_notes?: string;
  shipping_address: any;
  billing_address?: any;
  township_delivery_id?: number;
  shipping_address_id?: number;
  approved_by?: number;
  approved_at?: string;
  rejection_reason?: string;
  notes?: string;
  shipped_at?: string;
  delivered_at?: string;
  created_at: string;
  updated_at: string;
  user?: {
    id: number;
    name: string;
    email: string;
  };
  order_items?: OrderItem[];
  shipping_address_obj?: any;
  township_delivery?: any;
  approved_by_user?: any;
}

interface OrderItem {
  id: number;
  order_id: number;
  product_id: number;
  product_variant_id: number;
  product_name: string;
  variant_color: string;
  variant_size: string;
  quantity: number;
  price: number;
  total: number;
  product?: any;
  product_variant?: any;
  product_snapshot?: any;
}

interface OrderStatistics {
  total_orders: number;
  pending_approval: number;
  processing: number;
  shipped: number;
  delivered: number;
  cancelled: number;
  total_revenue: number;
  pending_revenue: number;
  this_month_orders: number;
  this_week_orders: number;
  pending_orders: number;
  paid_orders: number;
}

interface OrderFilters {
  search?: string;
  status?: string;
  payment_status?: string;
  payment_method?: string;
  date_from?: string;
  date_to?: string;
}

const AdminOrders = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [statistics, setStatistics] = useState<OrderStatistics>({ total_orders: 0, pending_approval: 0, processing: 0, shipped: 0, delivered: 0, cancelled: 0, total_revenue: 0, pending_revenue: 0, this_month_orders: 0, this_week_orders: 0, pending_orders: 0, paid_orders: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState<number | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAction, setPaymentAction] = useState<'approve' | 'reject' | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [editFormData, setEditFormData] = useState({
    status: '',
    payment_status: '',
    notes: '',
    payment_notes: ''
  });
  const [editItems, setEditItems] = useState<EditorLine[]>([]);
  const [savingEdit, setSavingEdit] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const filters: OrderFilters = {};
      if (searchTerm) filters.search = searchTerm;
      if (statusFilter) filters.status = statusFilter;
      if (paymentStatusFilter) filters.payment_status = paymentStatusFilter;
      if (paymentMethodFilter) filters.payment_method = paymentMethodFilter;
      if (dateFromFilter) filters.date_from = dateFromFilter;
      if (dateToFilter) filters.date_to = dateToFilter;
      
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const url = `/admin/orders${params.toString() ? `?${params.toString()}` : ''}`;
      
      const paginatedData = await apiService.get(url);
      
      // Handle different response formats
      let ordersArray;
      if (Array.isArray(paginatedData)) {
        // Direct array response
        ordersArray = paginatedData;
      } else if (paginatedData?.data && Array.isArray(paginatedData.data)) {
        // Laravel paginated response
        ordersArray = paginatedData.data;
      } else if (paginatedData?.orders && Array.isArray(paginatedData.orders)) {
        // Custom response format
        ordersArray = paginatedData.orders;
      } else {
        ordersArray = [];
      }
      
      setOrders(ordersArray);
    } catch (err: any) {
      console.error('Load orders error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, paymentStatusFilter, paymentMethodFilter, dateFromFilter, dateToFilter]);

  const loadStatistics = useCallback(async () => {
    try {
      const response = await apiService.get('/admin/orders/statistics');
      setStatistics(response.data);
    } catch (err: any) {
      console.error('Failed to load statistics:', err);
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, paymentStatusFilter, paymentMethodFilter, dateFromFilter, dateToFilter]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.status-dropdown-container')) {
        setShowStatusDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    setShowOrderDetails(true);
  };

  const handleUpdateStatus = async (orderId: number, status: string) => {
    try {
      setUpdatingStatus(orderId);
      await apiService.put(`/admin/orders/${orderId}/status`, { status });
      await loadOrders();
      await loadStatistics();
      setShowStatusDropdown(null);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update order status');
      alert(`Failed to update order status: ${err.response?.data?.message || err.message}`);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handlePaymentAction = async () => {
    if (!selectedOrder || !paymentAction) return;
    try {
      if (paymentAction === 'approve') {
        await apiService.put(`/admin/orders/${selectedOrder.id}/approve-payment`, {});
      } else {
        await apiService.put(`/admin/orders/${selectedOrder.id}/reject-payment`, {
          rejection_reason: rejectReason
        });
      }
      await loadOrders();
      await loadStatistics();
      setShowPaymentModal(false);
      setPaymentAction(null);
      setRejectReason('');
      setSelectedOrder(null);
    } catch (err: any) {
      setError(err.message || 'Failed to process payment');
    }
  };

  const openPaymentModal = (order: Order, action: 'approve' | 'reject') => {
    setSelectedOrder(order);
    setPaymentAction(action);
    setShowPaymentModal(true);
  };

  const handleEditOrder = (order: Order) => {
    setEditingOrder(order);
    setEditFormData({
      status: order.status,
      payment_status: order.payment_status,
      notes: order.notes || '',
      payment_notes: order.payment_notes || ''
    });
    // Seed the item editor from the order's current items (manual orders only).
    setEditItems(
      (order.order_items || [])
        .filter((it) => it.product_variant_id && it.variant_size)
        .map((it) => ({
          key: `${it.product_variant_id}-${it.variant_size}`,
          variant_id: it.product_variant_id,
          product_name: it.product_name,
          color: it.variant_color,
          size: it.variant_size,
          unit_price: Number(it.price),
          quantity: it.quantity,
          max_stock: 999999, // existing lines aren't stock-capped in the UI; backend re-validates
        }))
    );
    setShowEditModal(true);
  };

  const handleSaveEdit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!editingOrder) return;
    setSavingEdit(true);
    try {
      const isManual = (editingOrder as any).source === 'manual';

      // For manual orders, persist item changes first (adjusts stock).
      if (isManual) {
        if (editItems.length === 0) {
          setError('A manual order must have at least one item.');
          setSavingEdit(false);
          return;
        }
        await apiService.updateManualOrderItems(editingOrder.id, {
          items: editItems.map((l) => ({
            variant_id: l.variant_id,
            size: l.size,
            quantity: l.quantity,
          })),
        });
      }

      // Then persist status / payment / notes.
      await apiService.put(`/admin/orders/${editingOrder.id}`, editFormData);

      await loadOrders();
      await loadStatistics();
      setShowEditModal(false);
      setEditingOrder(null);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update order');
    } finally {
      setSavingEdit(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setPaymentStatusFilter('');
    setPaymentMethodFilter('');
    setDateFromFilter('');
    setDateToFilter('');
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <ClockIcon className="h-4 w-4" />;
      case 'processing': return <CreditCardIcon className="h-4 w-4" />;
      case 'shipped': return <TruckIcon className="h-4 w-4" />;
      case 'delivered': return <CheckCircleIcon className="h-4 w-4" />;
      case 'cancelled': return <XCircleIcon className="h-4 w-4" />;
      default: return <ClockIcon className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'shipped': return 'bg-purple-100 text-purple-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'paid': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'refunded': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return { date: 'N/A', time: '' };
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    return { date: dateStr, time: timeStr };
  };

  const formatCurrency = (amount: number) => `${new Intl.NumberFormat('en-US').format(amount)} MMK`;

  const paginatedOrders = orders.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(orders.length / itemsPerPage);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const getPageNumbers = useCallback(() => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
      } else if (currentPage >= totalPages - 2) {
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        for (let i = currentPage - 2; i <= currentPage + 2; i++) {
          pages.push(i);
        }
      }
    }
    
    return pages;
  }, [currentPage, totalPages]);

  const statCard = (Icon: React.ElementType, title: string, value: string | number, color: string) => (
    <div className="bg-white p-4 rounded-lg shadow-md flex items-center">
      <div className={`p-3 rounded-full mr-4 ${color}`}><Icon className="h-6 w-6 text-white" /></div>
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <p className="text-xl font-bold text-gray-800">{value}</p>
      </div>
    </div>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Orders Management</h1>

      {statistics && statistics.total_orders > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {statCard(ShoppingBagIcon, 'Total Orders', statistics.total_orders, 'bg-blue-500')}
          {statCard(CurrencyDollarIcon, 'Total Revenue', formatCurrency(statistics.total_revenue), 'bg-green-500')}
          {statCard(ClockIcon, 'Pending Orders', statistics.pending_orders, 'bg-yellow-500')}
          {statCard(CheckCircleIcon, 'Paid Orders', statistics.paid_orders, 'bg-purple-500')}
        </div>
      )}

      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <div className="relative xl:col-span-2">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by Order ID, Customer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm"
            />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full bg-white cursor-pointer px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm">
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select value={paymentStatusFilter} onChange={(e) => setPaymentStatusFilter(e.target.value)} className="w-full bg-white cursor-pointer px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm">
            <option value="">All Payment Statuses</option>
            <option value="pending">Pending</option>
            <option value="pending_approval">Pending Approval</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="paid">Paid</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>
          <input type="date" value={dateFromFilter} onChange={e => setDateFromFilter(e.target.value)} className="w-full bg-white cursor-pointer px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm" />
          <input type="date" value={dateToFilter} onChange={e => setDateToFilter(e.target.value)} className="w-full bg-white cursor-pointer px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm" />
        </div>
        <div className="flex justify-end mt-4">
            <button onClick={clearFilters} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">Clear Filters</button>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {loading ? (
          <div className="flex justify-center items-center p-20"><ArrowPathIcon className="h-8 w-8 text-gray-500 animate-spin" /></div>
        ) : error ? (
          <div className="text-center p-20 text-red-500">{error}</div>
        ) : orders.length === 0 ? (
            <div className="text-center p-20 text-gray-500">
              No orders found matching your filters.
            </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 table-fixed" style={{ minWidth: '1200px' }}>
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40 min-w-[160px]">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedOrders.map(order => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          <div className="text-sm font-medium text-gray-900">
                            #{order.order_number}
                          </div>
                          <div className="text-sm text-gray-500">
                            {order.order_items?.length || 0} items
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex flex-col">
                          <span className="font-medium flex items-center gap-2">
                            {order.customer_name || order.user?.name || 'Guest'}
                            {order.source === 'manual' && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700">
                                Social
                              </span>
                            )}
                          </span>
                          <span className="text-gray-500">
                            {order.customer_phone || order.user?.email || 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex flex-col">
                          <span>{formatDate(order.created_at).date}</span>
                          <span className="text-xs text-gray-400">{formatDate(order.created_at).time}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(order.total_amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="relative status-dropdown-container">
                          <button onClick={() => setShowStatusDropdown(showStatusDropdown === order.id ? null : order.id)} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                            {getStatusIcon(order.status)}
                            <span className="capitalize">{order.status}</span>
                            {updatingStatus === order.id ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <ChevronDownIcon className="h-4 w-4" />}
                          </button>
                          {showStatusDropdown === order.id && (
                            <div className="absolute z-10 mt-2 w-40 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                              <div className="py-1">
                                {['pending', 'processing', 'shipped', 'delivered', 'cancelled'].map(status => (
                                  <button key={status} onClick={() => handleUpdateStatus(order.id, status)} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2">
                                    {getStatusIcon(status)} <span className="capitalize">{status}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPaymentStatusColor(order.payment_status)}`}>
                          {order.payment_status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-40 min-w-[160px]">
                        <div className="flex items-center justify-start space-x-2">
                          <button 
                            onClick={() => handleViewOrder(order)} 
                            className="text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 p-2 rounded transition-colors flex-shrink-0"
                            title="View Order"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => handleEditOrder(order)} 
                            className="text-yellow-600 hover:text-yellow-900 hover:bg-yellow-50 p-2 rounded transition-colors flex-shrink-0"
                            title="Edit Order"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          {order.payment_method === 'manual_payment' && order.payment_status === 'pending_approval' && (
                            <>
                              <button 
                                onClick={() => openPaymentModal(order, 'approve')} 
                                className="text-green-600 hover:text-green-900 hover:bg-green-50 p-2 rounded transition-colors flex-shrink-0"
                                title="Approve Payment"
                              >
                                <CheckCircleIcon className="h-4 w-4" />
                              </button>
                              <button 
                                onClick={() => openPaymentModal(order, 'reject')} 
                                className="text-red-600 hover:text-red-900 hover:bg-red-50 p-2 rounded transition-colors flex-shrink-0"
                                title="Reject Payment"
                              >
                                <XCircleIcon className="h-4 w-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 bg-white">
              <div className="flex items-center justify-between">
                <div className="flex-1 flex justify-between sm:hidden">
                  {/* Mobile pagination */}
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing{' '}
                      <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span>
                      {' '}to{' '}
                      <span className="font-medium">
                        {Math.min(currentPage * itemsPerPage, orders.length)}
                      </span>
                      {' '}of{' '}
                      <span className="font-medium">{orders.length}</span>
                      {' '}results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 focus:z-10 focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500"
                      >
                        <span className="sr-only">Previous</span>
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                      {getPageNumbers().map((page) => (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-all duration-200 focus:z-10 focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500 ${
                            currentPage === page
                              ? 'z-10 bg-gray-600 border-gray-600 text-white hover:bg-gray-700'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 hover:text-gray-700 hover:border-gray-400'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 focus:z-10 focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500"
                      >
                        <span className="sr-only">Next</span>
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {showOrderDetails && selectedOrder && (
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-start justify-center p-4 z-50"
          onClick={() => setShowOrderDetails(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Order Details #{selectedOrder.order_number}</h3>
                <button
                  onClick={() => setShowOrderDetails(false)}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Customer Information */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Customer Information
                </label>
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                  <p className="text-sm text-gray-900">
                    {selectedOrder.customer_name || selectedOrder.user?.name || 'Guest'}
                    {selectedOrder.source === 'manual' && (
                      <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700">
                        Social
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-gray-600">{selectedOrder.customer_email || selectedOrder.user?.email || selectedOrder.shipping_address?.email || 'N/A'}</p>
                  <p className="text-sm text-gray-600">{selectedOrder.customer_phone || selectedOrder.shipping_address?.phone || 'N/A'}</p>
                  {selectedOrder.customer_address && (
                    <p className="text-sm text-gray-600 mt-1">{selectedOrder.customer_address}</p>
                  )}
                </div>
              </div>

              {/* Order Status */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Order Status
                </label>
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedOrder.status)}`}>
                    {selectedOrder.status}
                  </span>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Shipping Address
                </label>
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                  <p className="text-sm text-gray-900">{selectedOrder.shipping_address?.address_line_1 || 'N/A'}</p>
                  {selectedOrder.shipping_address?.address_line_2 && (
                    <p className="text-sm text-gray-900">{selectedOrder.shipping_address.address_line_2}</p>
                  )}
                  <p className="text-sm text-gray-600">
                    {selectedOrder.shipping_address?.township || 'N/A'}, {selectedOrder.shipping_address?.state_region || 'N/A'} {selectedOrder.shipping_address?.postal_code || ''}
                  </p>
                  <p className="text-sm text-gray-600">{selectedOrder.shipping_address?.country || 'N/A'}</p>
                </div>
              </div>

              {/* Payment Information */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Payment Information
                </label>
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 space-y-1">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Status:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(selectedOrder.payment_status)}`}>
                      {selectedOrder.payment_status.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Method:</span>
                    <span className="text-sm text-gray-900 capitalize">{selectedOrder.payment_method}</span>
                  </div>
                </div>
              </div>

              {/* Order Date */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Order Date
                </label>
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                  <div className="text-sm text-gray-900">{formatDate(selectedOrder.created_at).date}</div>
                  {formatDate(selectedOrder.created_at).time && (
                    <div className="text-xs text-gray-500">{formatDate(selectedOrder.created_at).time}</div>
                  )}
                </div>
              </div>

              {/* Payment Screenshot */}
              {selectedOrder.payment_screenshot && (
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Payment Screenshot
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                    <img 
                      src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://139.59.98.59'}/storage/${selectedOrder.payment_screenshot}`}
                      alt="Payment Screenshot"
                      className="w-32 h-32 object-cover rounded-md border border-gray-200 cursor-pointer hover:opacity-75 transition-opacity"
                      onClick={() => window.open(`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://139.59.98.59'}/storage/${selectedOrder.payment_screenshot}`, '_blank')}
                    />
                    <p className="text-xs text-gray-500 mt-2">Click to view full size</p>
                  </div>
                </div>
              )}

              {/* Order Notes */}
              {selectedOrder.notes && (
                <div className="md:col-span-2 space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Order Notes
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                    <p className="text-sm text-gray-900">{selectedOrder.notes}</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Order Items Section */}
            <div className="mt-8">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Order Items
              </label>
              <div className="border rounded-lg divide-y">
                {selectedOrder.order_items?.map((item: OrderItem) => {
                  // Use product snapshot data if available, fallback to current product data
                  const productData = item.product_snapshot || item.product || {};
                  const productImage = productData.primary_image || 
                                     productData.product_images?.[0] || 
                                     productData.hover_image || 
                                     item.product?.product_images?.[0] || 
                                     item.product?.hover_image || 
                                     '/placeholder.jpg';
                  const productName = productData.name || item.product_name;
                  const variantColor = item.variant_color || productData.variant_color || 'N/A';
                  
                  return (
                    <div key={item.id} className="flex items-center p-4">
                      <div className="flex-shrink-0 mr-4">
                        <img 
                          src={productImage} 
                          alt={productName} 
                          className="w-16 h-16 object-cover rounded-md border border-gray-200"
                          onError={(e) => {
                            e.currentTarget.src = '/placeholder.jpg';
                          }}
                        />
                      </div>
                      <div className="flex-grow">
                        <p className="text-sm font-medium text-gray-900">{productName}</p>
                        <div className="flex items-center gap-4 mt-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Color:</span>
                            <div className="flex items-center gap-1">
                              {variantColor !== 'N/A' && (
                                <div 
                                  className="w-4 h-4 rounded-full border border-gray-300" 
                                  style={{ backgroundColor: variantColor.toLowerCase() }}
                                  title={variantColor}
                                />
                              )}
                              <span className="text-xs text-gray-700 capitalize">{variantColor}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-500">Size:</span>
                            <span className="text-xs text-gray-700 uppercase">{item.variant_size || 'N/A'}</span>
                          </div>
                        </div>
                        {productData.product_code && (
                          <p className="text-xs text-gray-400 mt-1">SKU: {productData.product_code}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-900">{formatCurrency(item.price)} × {item.quantity}</p>
                        <p className="text-sm font-medium text-gray-900">{formatCurrency(item.total)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Order Summary */}
            <div className="mt-8 flex justify-end">
              <div className="w-64 space-y-2 p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="text-gray-900">{formatCurrency(selectedOrder.subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Delivery Fee:</span>
                  <span className="text-gray-900">{formatCurrency(selectedOrder.delivery_fee || 0)}</span>
                </div>
                <div className="flex justify-between text-lg font-medium border-t pt-2">
                  <span className="text-gray-900">Total:</span>
                  <span className="text-gray-900">{formatCurrency(selectedOrder.total_amount)}</span>
                </div>
              </div>
            </div>
            
              {/* Actions */}
              <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowOrderDetails(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPaymentModal && selectedOrder && (
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setShowPaymentModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900 capitalize">{paymentAction} Payment</h3>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="mt-4">
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to {paymentAction} the payment for order <strong>#{selectedOrder.order_number}</strong>?
              </p>
              {selectedOrder.payment_screenshot && (
                <div className="mb-4">
                  <a 
                    href={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://139.59.98.59'}/storage/${selectedOrder.payment_screenshot}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1 text-sm"
                  >
                    <PhotoIcon className="h-4 w-4" />
                    View Payment Screenshot
                  </a>
                </div>
              )}
              {paymentAction === 'reject' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for rejection (optional)
                  </label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Enter rejection reason..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                    rows={3}
                  />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <button 
                onClick={() => setShowPaymentModal(false)} 
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button 
                onClick={handlePaymentAction} 
                className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                  paymentAction === 'approve' 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                Confirm {paymentAction && paymentAction.charAt(0).toUpperCase() + paymentAction.slice(1)}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && editingOrder && (
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-start justify-center p-4 z-50"
          onClick={() => setShowEditModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Edit Order #{editingOrder.order_number}</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

            <form onSubmit={handleSaveEdit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Order Status */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Order Status *
                  </label>
                  <select 
                    value={editFormData.status}
                    onChange={(e) => setEditFormData({...editFormData, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998]"
                  >
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {/* Payment Status */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Payment Status *
                  </label>
                  <select 
                    value={editFormData.payment_status}
                    onChange={(e) => setEditFormData({...editFormData, payment_status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998]"
                  >
                    <option value="pending">Pending</option>
                    <option value="pending_approval">Pending Approval</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="paid">Paid</option>
                    <option value="failed">Failed</option>
                    <option value="refunded">Refunded</option>
                  </select>
                </div>

                {/* Manual order item editor — adjusts stock automatically */}
                {(editingOrder as any).source === 'manual' && (
                  <div className="md:col-span-2">
                    <ManualOrderItemsEditor value={editItems} onChange={setEditItems} />
                    <p className="text-xs text-gray-400 mt-1">
                      Changing items updates stock automatically (adds back removed/reduced, deducts added/increased).
                    </p>
                  </div>
                )}

                {/* Order Notes */}
                <div className="md:col-span-2 space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Order Notes
                  </label>
                  <textarea
                    value={editFormData.notes}
                    onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998]"
                    placeholder="Add notes about this order..."
                  />
                </div>

                {/* Payment Notes */}
                <div className="md:col-span-2 space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Payment Notes
                  </label>
                  <textarea
                    value={editFormData.payment_notes}
                    onChange={(e) => setEditFormData({...editFormData, payment_notes: e.target.value})}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998]"
                    placeholder="Add payment-related notes..."
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingEdit}
                  className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
                >
                  {savingEdit ? 'Saving...' : 'Update Order'}
                </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;

